import React, {
    createContext,
    MutableRefObject,
    useCallback,
    useContext,
    useEffect,
    useRef,
    useState,
} from 'react';
import { Outlet } from 'react-router-dom';

type TPermissionsContext = {
    audioRef: MutableRefObject<HTMLAudioElement>;
    isPlayable: boolean;
    isPlaying: boolean;
    cleanup: () => void;
    allowAudio: () => Promise<void>;
};

const AudioContext = createContext<TPermissionsContext | null>(null);

export const useAudioContext = () => {
    const context = useContext(AudioContext);
    if (!context) {
        throw new Error('useAudioContext must be used within a AudioProvider');
    }
    return context;
};

const AudioProvider = () => {
    const [isPlaying, setIsPlaying] = useState<boolean>(false);
    const [isPlayable, setIsPlayable] = useState(false);

    const audioRef = useRef<HTMLAudioElement>(
        (() => {
            const audio = new Audio();
            audio.loop = true;
            return audio;
        })(),
    );

    const allowAudio = async () => {
        audioRef.current.volume = 0;
        await audioRef.current.play();
        setTimeout(() => {
            audioRef.current.pause();
            audioRef.current.volume = 1;
            audioRef.current.currentTime = 0;
        });
    };

    useEffect(() => {
        const audio = audioRef.current;
        if (!audio) return;

        const handleCanPlayThrough = () => setIsPlayable(true);
        const handlePlaying = () => setIsPlaying(true);
        const handlePause = () => setIsPlaying(false);
        const handleError = () => {
            setIsPlaying(false);
            setIsPlayable(false);
            console.warn('Audio failed to load:', audio.src);
        };

        audio.addEventListener('canplaythrough', handleCanPlayThrough);
        audio.addEventListener('play', handlePlaying);
        audio.addEventListener('pause', handlePause);
        audio.addEventListener('error', handleError);

        return () => {
            audio.removeEventListener('canplaythrough', handleCanPlayThrough);
            audio.removeEventListener('play', handlePlaying);
            audio.removeEventListener('pause', handlePause);
            audio.removeEventListener('error', handleError);
        };
    }, []);

    const cleanup = useCallback(() => {
        if (audioRef.current) {
            audioRef.current.pause();
        }
        setIsPlaying(false);
        setIsPlayable(false);
    }, []);

    return (
        <>
            <AudioContext.Provider value={{ audioRef, isPlayable, isPlaying, cleanup, allowAudio }}>
                <Outlet />
            </AudioContext.Provider>
        </>
    );
};

export default AudioProvider;
